import { useState, useEffect } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToEmpleados, addEmpleado, deleteEmpleado, updateEmpleado, Empleado,
  ocultarEmpleado, mostrarEmpleado,
} from "../../firebase/firebaseService";
import {
  FaUsers, FaPlus, FaTrash, FaSearch, FaTimes,
  FaUserCheck, FaDollarSign, FaCalendarAlt,
  FaEnvelope, FaMapMarkerAlt, FaEdit, FaPhone, FaEyeSlash, FaEye,
} from "react-icons/fa";

const fmt = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const toInput = (d: Date) => {
  try { return d.toISOString().split("T")[0]; } catch { return ""; }
};

const emptyForm = {
  nombre: "", apellidos: "", email: "", telefono: "", direccion: "",
  fechaIngreso: new Date().toISOString().split("T")[0],
  costoPorHora: "",
};

export default function EmpleadosAdmin() {
  const [empleados,    setEmpleados]    = useState<Empleado[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);

  // Modal agregar
  const [showModal,    setShowModal]    = useState(false);
  const [form,         setForm]         = useState(emptyForm);
  const [errors,       setErrors]       = useState<Record<string, string>>({});

  // Modal editar
  const [editTarget,   setEditTarget]   = useState<Empleado | null>(null);
  const [editForm,     setEditForm]     = useState(emptyForm);
  const [editErrors,   setEditErrors]   = useState<Record<string, string>>({});
  const [updating,     setUpdating]     = useState(false);

  // Modal eliminar
  const [deleteTarget, setDeleteTarget] = useState<Empleado | null>(null);

  const [search, setSearch] = useState("");
  const [mostrarOcultos, setMostrarOcultos] = useState(false);

  useEffect(() => {
    const unsub = subscribeToEmpleados((d) => { setEmpleados(d); setLoading(false); });
    return () => unsub();
  }, []);

  // ── Agregar ──────────────────────────────────────────────────────────────
  const canSave = form.nombre.trim() && form.apellidos.trim() && form.costoPorHora !== "";

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!form.nombre.trim())      e.nombre      = "El nombre es obligatorio";
    if (!form.apellidos.trim())   e.apellidos   = "Los apellidos son obligatorios";
    if (form.costoPorHora === "") e.costoPorHora = "El costo por hora es obligatorio";
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload: any = {
        nombre: form.nombre.trim(), apellidos: form.apellidos.trim(),
        fechaIngreso: new Date(form.fechaIngreso + "T12:00:00"),
        costoPorHora: Number(form.costoPorHora), activo: true,
      };
      if (form.email.trim())     payload.email     = form.email.trim();
      if (form.telefono.trim())  payload.telefono  = form.telefono.trim();
      if (form.direccion.trim()) payload.direccion = form.direccion.trim();
      await addEmpleado(payload);
      setShowModal(false); setForm(emptyForm); setErrors({});
    } finally { setSaving(false); }
  };

  // ── Editar ───────────────────────────────────────────────────────────────
  const openEdit = (emp: Empleado) => {
    setEditTarget(emp);
    setEditForm({
      nombre:      emp.nombre,
      apellidos:   emp.apellidos,
      email:       emp.email      || "",
      telefono:    emp.telefono   || "",
      direccion:   emp.direccion  || "",
      fechaIngreso: toInput(emp.fechaIngreso),
      costoPorHora: emp.costoPorHora > 0 ? String(emp.costoPorHora) : "",
    });
    setEditErrors({});
  };

  const canUpdate =
    editForm.nombre.trim() && editForm.apellidos.trim() && editForm.costoPorHora !== "";

  const handleUpdate = async () => {
    const e: Record<string, string> = {};
    if (!editForm.nombre.trim())      e.nombre      = "El nombre es obligatorio";
    if (!editForm.apellidos.trim())   e.apellidos   = "Los apellidos son obligatorios";
    if (editForm.costoPorHora === "") e.costoPorHora = "El costo por hora es obligatorio";
    if (Object.keys(e).length) { setEditErrors(e); return; }
    if (!editTarget?.id) return;
    setUpdating(true);
    try {
      const payload: any = {
        nombre:      editForm.nombre.trim(),
        apellidos:   editForm.apellidos.trim(),
        fechaIngreso: new Date(editForm.fechaIngreso + "T12:00:00"),
        costoPorHora: Number(editForm.costoPorHora),
      };
      if (editForm.email.trim())     payload.email     = editForm.email.trim();
      if (editForm.telefono.trim())  payload.telefono  = editForm.telefono.trim();
      if (editForm.direccion.trim()) payload.direccion = editForm.direccion.trim();
      await updateEmpleado(editTarget.id, payload);
      setEditTarget(null);
    } finally { setUpdating(false); }
  };

  // ── Eliminar ─────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    await deleteEmpleado(deleteTarget.id);
    setDeleteTarget(null);
  };

  // ── Ocultar/Mostrar ──────────────────────────────────────────────────────────
  const handleOcultar = async (id: string) => {
    await ocultarEmpleado(id);
  };

  const handleMostrar = async (id: string) => {
    await mostrarEmpleado(id);
  };

  // ── Filtrado ─────────────────────────────────────────────────────────────────
  const empleadosVisibles = empleados.filter((e) => !e.oculto);
  const empleadosOcultos = empleados.filter((e) => e.oculto);

  const filtered = empleadosVisibles.filter((e) =>
    `${e.nombre} ${e.apellidos} ${e.email ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredOcultos = empleadosOcultos.filter((e) =>
    `${e.nombre} ${e.apellidos} ${e.email ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const activos  = empleadosVisibles.filter((e) => e.activo).length;
  const avgCosto = empleadosVisibles.length
    ? empleadosVisibles.reduce((s, e) => s + (e.costoPorHora || 0), 0) / empleadosVisibles.length : 0;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />
      <div className="flex-1 overflow-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">Empleados</h1>
              <p className="text-violet-200 text-sm mt-1">Gestiona el equipo de trabajo de Gobies</p>
            </div>
            <button
              onClick={() => { setShowModal(true); setForm(emptyForm); setErrors({}); }}
              className="flex items-center gap-2 bg-white text-violet-700 font-bold px-5 py-2.5 rounded-xl shadow-lg hover:bg-violet-50 transition-all text-[15px]"
            >
              <FaPlus size={14} /> Nuevo Empleado
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {[
              { icon: FaUsers,      label: "Total empleados",  value: empleados.length },
              { icon: FaUserCheck,  label: "Activos",          value: activos },
              { icon: FaDollarSign, label: "Costo prom./hora", value: `$${avgCosto.toFixed(2)}` },
            ].map((s) => (
              <div key={s.label} className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <s.icon className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-violet-200 text-[13px] font-medium">{s.label}</p>
                  <p className="text-white text-2xl font-bold">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 lg:p-8">
          <div className="relative mb-6 max-w-md">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input type="text" placeholder="Buscar por nombre o email..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 shadow-sm"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaUsers size={40} className="mb-3 text-gray-200" />
                <p className="font-medium text-[15px]">No hay empleados registrados</p>
                <p className="text-[13px] mt-1">Agrega el primero con el botón de arriba</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["Empleado","Contacto","Dirección","Ingreso","$/hora","Estado",""].map((h, i) => (
                        <th key={i} className={`text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider ${i === 2 ? "hidden lg:table-cell" : i === 1 ? "hidden md:table-cell" : ""}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((emp) => (
                      <tr
                        key={emp.id}
                        onClick={() => openEdit(emp)}
                        className="hover:bg-violet-50/40 transition-colors cursor-pointer group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {emp.nombre[0]}{emp.apellidos[0]}
                            </div>
                            <div>
                              <span className="text-[15px] font-semibold text-gray-800">{emp.nombre} {emp.apellidos}</span>
                              <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <FaEdit size={12} className="inline text-violet-400" />
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <div className="flex flex-col gap-1">
                            {emp.email && (
                              <span className="flex items-center gap-1.5 text-[13px] text-gray-500">
                                <FaEnvelope size={10} className="text-gray-400" />{emp.email}
                              </span>
                            )}
                            {emp.telefono && (
                              <span className="flex items-center gap-1.5 text-[13px] text-gray-500">
                                <FaPhone size={10} className="text-gray-400" />{emp.telefono}
                              </span>
                            )}
                            {!emp.email && !emp.telefono && (
                              <span className="text-gray-300 text-[15px]">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          {emp.direccion
                            ? <span className="flex items-center gap-1.5 text-[13px] text-gray-500"><FaMapMarkerAlt size={10} className="text-gray-400 shrink-0" /><span className="truncate max-w-[180px]">{emp.direccion}</span></span>
                            : <span className="text-gray-300 text-[15px]">—</span>}
                        </td>
                        <td className="px-6 py-4">
                          <span className="flex items-center gap-1.5 text-[13px] text-gray-500">
                            <FaCalendarAlt size={10} className="text-gray-400" />{fmt(emp.fechaIngreso)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[15px] font-semibold text-gray-800">
                            {emp.costoPorHora > 0 ? `$${emp.costoPorHora.toFixed(2)}` : "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-semibold ${emp.activo ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {emp.activo ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(ev) => { ev.stopPropagation(); handleOcultar(emp.id!); }}
                              className="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                              title="Ocultar empleado"
                            >
                              <FaEyeSlash size={13} />
                            </button>
                            <button
                              onClick={(ev) => { ev.stopPropagation(); setDeleteTarget(emp); }}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Eliminar empleado"
                            >
                              <FaTrash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-[13px] text-gray-400 mt-3 px-1">
            Haz clic en un empleado para editar su información
          </p>

          {/* ══ Empleados Ocultos ══════════════════════════════════════════════ */}
          {empleadosOcultos.length > 0 && (
            <div className="mt-6">
              <button
                onClick={() => setMostrarOcultos(!mostrarOcultos)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors text-sm font-medium"
              >
                {mostrarOcultos ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                {mostrarOcultos ? 'Ocultar' : 'Mostrar'} empleados archivados ({empleadosOcultos.length})
              </button>

              {mostrarOcultos && (
                <div className="mt-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-500 mb-3 flex items-center gap-2">
                    <FaEyeSlash size={14} />
                    Empleados Archivados
                  </h3>
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Empleado</th>
                          <th className="px-6 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Contacto</th>
                          <th className="px-6 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Fecha Ingreso</th>
                          <th className="px-6 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Costo/Hora</th>
                          <th className="px-6 py-3 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {empleadosOcultos.map((emp) => (
                          <tr key={emp.id} className="hover:bg-gray-50 transition-colors opacity-60">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold text-sm">
                                  {emp.nombre.charAt(0)}{emp.apellidos.charAt(0)}
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-600 text-sm">{emp.nombre} {emp.apellidos}</div>
                                  <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                    <FaUserCheck size={10} />
                                    {emp.activo ? "Activo" : "Inactivo"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {emp.email && (
                                <div className="flex items-center gap-1.5 text-[12px] text-gray-500 mb-1">
                                  <FaEnvelope size={10} className="text-gray-400" />
                                  {emp.email}
                                </div>
                              )}
                              {emp.telefono && (
                                <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                                  <FaPhone size={10} className="text-gray-400" />
                                  {emp.telefono}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                                <FaCalendarAlt size={10} className="text-gray-400" />
                                {new Date(emp.fechaIngreso).toLocaleDateString("es-MX")}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-600">
                                <FaDollarSign size={10} className="text-gray-400" />
                                {emp.costoPorHora.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => handleMostrar(emp.id!)}
                                className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                title="Restaurar empleado"
                              >
                                <FaEye size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[12px] text-gray-400 mt-3">
                    Los empleados archivados mantienen su historial de pagos y servicios
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══ Modal: Nuevo Empleado ══════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Nuevo Empleado</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">* Nombre, apellidos y costo/hora son obligatorios</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Nombre *</label>
                  <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Juan"
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${errors.nombre ? "border-red-400" : "border-gray-200"}`} />
                  {errors.nombre && <p className="text-red-500 text-[13px] mt-1">{errors.nombre}</p>}
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Apellidos *</label>
                  <input type="text" value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} placeholder="García"
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${errors.apellidos ? "border-red-400" : "border-gray-200"}`} />
                  {errors.apellidos && <p className="text-red-500 text-[13px] mt-1">{errors.apellidos}</p>}
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="empleado@email.com"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Teléfono</label>
                <input type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Dirección</label>
                <input type="text" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle, ciudad, estado"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha de Ingreso</label>
                  <input type="date" value={form.fechaIngreso} onChange={(e) => setForm({ ...form, fechaIngreso: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Costo por hora ($) *</label>
                  <input type="number" min="0" step="0.01" value={form.costoPorHora} onChange={(e) => setForm({ ...form, costoPorHora: e.target.value })} placeholder="0.00"
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${errors.costoPorHora ? "border-red-400" : "border-gray-200"}`} />
                  {errors.costoPorHora && <p className="text-red-500 text-[13px] mt-1">{errors.costoPorHora}</p>}
                </div>
              </div>
            </div>
            <p className="text-[13px] text-gray-400 text-center px-6 pb-2">* Obligatorios: Nombre, Apellidos y Costo por hora</p>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSubmit} disabled={!canSave || saving}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-violet-200 hover:from-violet-700 hover:to-indigo-700 transition-all">
                {saving ? "Guardando..." : "Guardar Empleado"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Editar Empleado ════════════════════════════════════════════ */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <FaEdit size={14} className="text-violet-500" />
                  <span className="text-[13px] font-bold text-violet-500 uppercase tracking-wide">Editar empleado</span>
                </div>
                <h2 className="text-lg font-bold text-gray-800">{editTarget.nombre} {editTarget.apellidos}</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">Modifica los datos y haz clic en Actualizar</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Nombre *</label>
                  <input type="text" value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${editErrors.nombre ? "border-red-400" : "border-gray-200"}`} />
                  {editErrors.nombre && <p className="text-red-500 text-[13px] mt-1">{editErrors.nombre}</p>}
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Apellidos *</label>
                  <input type="text" value={editForm.apellidos} onChange={(e) => setEditForm({ ...editForm, apellidos: e.target.value })}
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${editErrors.apellidos ? "border-red-400" : "border-gray-200"}`} />
                  {editErrors.apellidos && <p className="text-red-500 text-[13px] mt-1">{editErrors.apellidos}</p>}
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="empleado@email.com"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Teléfono</label>
                <input type="tel" value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Dirección</label>
                <input type="text" value={editForm.direccion} onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })} placeholder="Calle, ciudad, estado"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha de Ingreso</label>
                  <input type="date" value={editForm.fechaIngreso} onChange={(e) => setEditForm({ ...editForm, fechaIngreso: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Costo por hora ($) *</label>
                  <input type="number" min="0" step="0.01" value={editForm.costoPorHora} onChange={(e) => setEditForm({ ...editForm, costoPorHora: e.target.value })} placeholder="0.00"
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400 ${editErrors.costoPorHora ? "border-red-400" : "border-gray-200"}`} />
                  {editErrors.costoPorHora && <p className="text-red-500 text-[13px] mt-1">{editErrors.costoPorHora}</p>}
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setEditTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleUpdate} disabled={!canUpdate || updating}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-violet-200 hover:from-violet-700 hover:to-indigo-700 transition-all">
                {updating ? "Actualizando..." : "Actualizar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Eliminar ═══════════════════════════════════════════════════ */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><FaTrash size={20} className="text-red-500" /></div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">¿Eliminar empleado?</h3>
            <p className="text-gray-500 text-[15px] mb-6">Vas a eliminar a <strong>{deleteTarget.nombre} {deleteTarget.apellidos}</strong>. Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-[15px] hover:bg-red-600 shadow-lg shadow-red-200">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
